import React, { useEffect, useRef, useState } from 'react';
import { COLORS } from '../../../../utils/colors';
import { firebaseLargeFileUploadHelper, openDillToast } from '../../../../utils/helpers';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { uploadManualCreditReport } from '../../../../redux/services/creditReportService';
import { getSupplierCreditApplication } from '../../../../redux/services/creditApplicationsService';
import moment from 'moment';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillField } from '../../../general/Dill/DillField';
import { DillInput } from '../../../general/Dill/DillInput';
import { DillButton } from '../../../general/Dill/DillButton';
import StateDropDown from './StateDropDown';
import { CreditApplication } from '@dill/dill-shared/dist/src';

const UploadManualCreditReportModal = ({
  open,
  handleClose,
  creditApplicationId,
  internalFormSubmissionId,
  onReportsChanged,
  creditApplication,
  fieldValues,
}: {
  open: boolean;
  handleClose: () => void;
  creditApplicationId: string;
  // On an internal form's review page: reports are kept on that submission, and
  // onReportsChanged reloads them instead of refetching a credit application.
  internalFormSubmissionId?: string;
  onReportsChanged?: () => void;
  creditApplication?: CreditApplication | null;
  // Prefill source when there's no credit application (an internal form's answers).
  fieldValues?: CreditApplication['fieldValues'];
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [reportName, setReportName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Prefill from the credit application's business info fields when the modal opens.
  useEffect(() => {
    if (!open) return;
    const getFieldValue = (id: string) => {
      const value = (creditApplication?.fieldValues ?? fieldValues)?.find(
        (f) => f.id === id
      )?.value;
      return typeof value === 'string' ? value : '';
    };
    setReportName(getFieldValue('customer-or-company-name'));
    setAddress1(getFieldValue('company-address'));
    setAddress2(getFieldValue('company-address-2'));
    setCity(getFieldValue('company-city'));
    setState(getFieldValue('company-state'));
    setZipCode(getFieldValue('company-zip'));
  }, [open, creditApplication]);

  // Refreshing/closing the tab mid-upload silently aborts it with no persisted record, so warn
  // before the browser tears down the page.
  useEffect(() => {
    if (!uploading) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploading]);

  const handleCloseModal = () => {
    if (uploading) {
      openDillToast({
        message: 'Please wait for the upload to finish before closing.',
        type: 'ERROR',
      });
      return;
    }
    setSelectedFile(null);
    setReportName('');
    setAddress1('');
    setAddress2('');
    setCity('');
    setState('');
    setZipCode('');
    setUploadProgress(0);
    handleClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !currentSupplier?.id) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formattedDate = moment(new Date()).format('MM-DD-YYYY').toUpperCase();
      const fileNameWithExtension = `${formattedDate}__${selectedFile.name}`;
      const storagePath = `manual-credit-reports/${currentSupplier.id}/${
        creditApplicationId || internalFormSubmissionId
      }`;

      const blobUrl = URL.createObjectURL(selectedFile);
      const fileUrl = await firebaseLargeFileUploadHelper({
        blobUrl,
        storagePath,
        fileNameWithExtension,
        onProgress: (progress) => setUploadProgress(Math.round(progress)),
      });
      URL.revokeObjectURL(blobUrl);

      if (!fileUrl) {
        openDillToast({ message: 'Failed to upload file to storage', type: 'ERROR' });
        setUploading(false);
        return;
      }

      const result = await dispatch(
        uploadManualCreditReport({
          supplierId: currentSupplier.id,
          creditApplicationId,
          internalFormSubmissionId,
          fileName: reportName || fileNameWithExtension,
          fileUrl,
          address1: address1 || undefined,
          address2: address2 || undefined,
          city: city || undefined,
          state: state || undefined,
          zipCode: zipCode || undefined,
        })
      );

      if (result?.type.includes('fulfilled')) {
        openDillToast({ message: 'Credit report uploaded successfully', type: 'SUCCESS' });
        if (onReportsChanged) onReportsChanged();
        else
          dispatch(
            getSupplierCreditApplication({
              supplierId: currentSupplier.id,
              creditApplicationId,
              hashedType: 'encrypted',
              fetchType: 'detailed',
              skipLoading: 'true',
              skipCache: 'true',
            })
          );
        handleCloseModal();
      } else {
        const payload: any = result?.payload;
        openDillToast({
          message: typeof payload === 'string' ? payload : 'Failed to save credit report.',
          type: 'ERROR',
        });
      }
    } catch (error: any) {
      openDillToast({ message: error?.message ?? 'Upload failed', type: 'ERROR' });
    }

    setUploading(false);
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Upload Credit Report"
      footer={
        <>
          <DillButton
            label="Cancel"
            variant="secondary"
            onClick={handleCloseModal}
            isDisabled={uploading}
          />
          <DillButton
            label="Upload"
            variant="primary"
            onClick={handleUpload}
            isDisabled={!selectedFile || uploading}
            isLoading={uploading}
          />
        </>
      }>
      {/* Name */}
      <DillField label="Business / Customer Name">
        <DillInput value={reportName} onChange={setReportName} placeholder="e.g. Dill Corp" />
      </DillField>

      {/* Address Line 1 */}
      <DillField label="Address Line 1">
        <DillInput value={address1} onChange={setAddress1} placeholder="123 Main St" />
      </DillField>

      {/* Address Line 2 */}
      <DillField label="Address Line 2 (optional)">
        <DillInput value={address2} onChange={setAddress2} placeholder="Suite 100" />
      </DillField>

      {/* City / State / ZIP */}
      <div className="flex gap-3">
        <DillField label="City" className="w-full">
          <DillInput value={city} onChange={setCity} placeholder="New York" />
        </DillField>
        <StateDropDown
          isRequired={false}
          value={state}
          onTextChange={setState}
          containerClassName="h-[36px] mt-[2px] border-gray-300 "
          labelTextClassName="text-[11px] font-medium text-gray-400 uppercase tracking-wide"
        />
        <DillField label="ZIP Code" className="w-full">
          <DillInput value={zipCode} onChange={setZipCode} placeholder="10001" />
        </DillField>
      </div>

      {/* File picker */}
      <DillField label="PDF File">
        <div
          className="border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-blue-400 transition-colors"
          onClick={() => fileInputRef.current?.click()}>
          {selectedFile ? (
            <>
              <p className="text-sm font-medium text-center">{selectedFile.name}</p>
              <p className="text-xs text-gray-400">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
              <p className="text-xs text-blue-500">Click to change</p>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-400">Click to select a PDF</p>
              <p className="text-xs text-gray-400">Only PDF files are supported</p>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={handleFileChange}
        />
      </DillField>

      {/* Progress bar */}
      {uploading && (
        <div className="flex flex-col gap-1">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-blue-500 h-1.5 rounded-full transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 text-right">{uploadProgress}%</p>
        </div>
      )}
    </DillModal>
  );
};

export default UploadManualCreditReportModal;
